// cameraStream.js
// Fonte ÚNICA da abertura de câmera por getUserMedia no app (CustomCamera do
// registro de treino / chat da VITA / scanfood / laudo, e CameraMobile da
// avaliação corporal).
//
// POR QUE ISTO EXISTE — a frontal "não funcionava":
//   • `facingMode: { ideal: "user" }` NÃO é obrigatório. Quando o aparelho não
//     consegue abrir a frontal, o navegador devolve SILENCIOSAMENTE a traseira:
//     o botão de virar parecia morto (e a UI ainda espelhava a imagem, achando
//     que estava na frontal).
//   • Android abre UMA câmera por vez. Qualquer stream órfão ainda segurando a
//     traseira faz o pedido da frontal falhar com NotReadableError.
//
// A escada abaixo pede primeiro de forma OBRIGATÓRIA (`exact`), relaxa só
// quando o `exact` não é suportado e CONFERE no fim qual câmera veio — pelo
// dado ESTRUTURADO do track (`getSettings().facingMode`), nunca pelo `label`
// (texto de exibição do fabricante).
import { ehErroDePermissao } from "./permissoesApp";

/** Solta o hardware. Nunca lança — stream já parado/nulo é no-op. */
export const pararStream = (stream) => {
  try { stream?.getTracks?.().forEach((t) => t.stop()); } catch { /* já parado */ }
};

/** "user" | "environment" | null (aparelho não reporta). */
export const facingDoStream = (stream) => {
  const f = stream?.getVideoTracks?.()[0]?.getSettings?.().facingMode;
  return f === "user" || f === "environment" ? f : null;
};

// Aceita o stream a menos que ele PROVE ser a outra câmera. Vários WebViews
// (Android antigo, alguns WKWebView) simplesmente não reportam facingMode:
// "null" ali é desconhecido, não é erro — recusar quebraria a troca neles.
const streamServe = (stream, facing) => {
  const real = facingDoStream(stream);
  return real === null || real === facing;
};

const RESOLUCAO_IDEAL = { width: { ideal: 1920 }, height: { ideal: 1080 } };

/**
 * Abre a câmera `facing` ("user" | "environment").
 * Resolve com o MediaStream ou rejeita — nunca devolve a câmera errada quando
 * o aparelho sabe dizer qual é.
 */
export const abrirStream = async (facing) => {
  const tentativas = [
    { facingMode: { exact: facing }, ...RESOLUCAO_IDEAL },
    // Alguns aparelhos só têm a frontal em resoluções menores e recusam o par
    // exact + 1920×1080; sem o pedido de resolução a MESMA câmera abre.
    { facingMode: { exact: facing } },
    // WebView que não implementa `exact` para facingMode: aqui o resultado é
    // conferido por streamServe antes de ser aceito.
    { facingMode: facing, ...RESOLUCAO_IDEAL },
  ];

  let ultimoErro = null;
  for (const video of tentativas) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
      if (streamServe(stream, facing)) return stream;
      // Veio a câmera errada: solta ANTES de tentar outra rota, senão ela fica
      // segurando o hardware e a próxima abertura falha.
      pararStream(stream);
    } catch (err) {
      ultimoErro = err;
      // Permissão negada não melhora tentando de novo — sobe na hora.
      if (ehErroDePermissao(err)) throw err;
    }
  }

  // Última rota: varre os dispositivos de vídeo e abre por deviceId, checando o
  // facingMode REAL de cada um. Só chega aqui depois de já ter havido um pedido
  // de permissão, então enumerateDevices devolve a lista com deviceId utilizável.
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    for (const d of devices) {
      if (d.kind !== "videoinput" || !d.deviceId) continue;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: d.deviceId } }, audio: false,
        });
        if (facingDoStream(stream) === facing) return stream;
        pararStream(stream);
      } catch (err) { ultimoErro = err; }
    }
  } catch (err) { ultimoErro = err; }

  throw ultimoErro || Object.assign(
    new Error(`Câmera "${facing}" indisponível.`),
    { name: "NotFoundError" },
  );
};

const cameraStream = { pararStream, facingDoStream, abrirStream };
export default cameraStream;
